import React, { useState, useCallback } from 'react';
import { Location } from '../../../types';
import { X } from 'lucide-react';

interface PathCreatorProps {
  locations: Location[];
  onAddPath: (startLocationId: string, endLocationId: string, waypoints: { x: number; y: number }[]) => void;
  onCancel: () => void;
}

export const PathCreator: React.FC<PathCreatorProps> = ({
  locations,
  onAddPath,
  onCancel,
}) => {
  const [startLocation, setStartLocation] = useState<Location | null>(null);
  const [endLocation, setEndLocation] = useState<Location | null>(null);
  const [waypoints, setWaypoints] = useState<{ x: number; y: number }[]>([]);
  const [_isAddingWaypoint, _setIsAddingWaypoint] = useState(false);

  const handleMapClick = useCallback((e: React.MouseEvent) => {
    if (!startLocation) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if we clicked on an end location
    const clickedLocation = locations.find(loc => {
      const dx = loc.position.x - x;
      const dy = loc.position.y - y;
      return Math.sqrt(dx * dx + dy * dy) < 20; // 20px radius for clicking
    });

    if (clickedLocation && clickedLocation.id !== startLocation.id) {
      // We clicked on a valid end location
      setEndLocation(clickedLocation);
      onAddPath(startLocation.id, clickedLocation.id, waypoints);
      onCancel();
      return;
    }

    // Add a waypoint
    setWaypoints([...waypoints, { x, y }]);
  }, [startLocation, waypoints, locations, onAddPath, onCancel]);

  const handleLocationClick = useCallback((location: Location) => {
    if (!startLocation) {
      setStartLocation(location);
    }
  }, [startLocation]);

  const handleDeleteWaypoint = useCallback((index: number) => {
    setWaypoints(waypoints.filter((_, i) => i !== index));
  }, [waypoints]);

  const handleUndoLastWaypoint = useCallback(() => {
    setWaypoints(waypoints.slice(0, -1));
  }, [waypoints]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-stone-800">Create Path</h2>
            <p className="text-sm text-stone-600">
              {!startLocation 
                ? "Click a location to start the path"
                : "Click on the map to add waypoints, then click the destination location"}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-stone-500 hover:text-stone-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div 
          className="relative bg-stone-50 rounded-lg border border-stone-200 aspect-video mb-4"
          onClick={handleMapClick}
        >
          {/* Draw the path */}
          <svg className="absolute inset-0 w-full h-full">
            <polyline
              points={[
                startLocation?.position,
                ...waypoints,
                endLocation?.position
              ]
                .filter((p): p is { x: number; y: number } => p !== undefined)
                .map(p => `${p.x},${p.y}`)
                .join(' ')}
              fill="none"
              stroke="#78716c"
              strokeWidth={2}
              strokeDasharray="4,4"
            />
          </svg>

          {/* Draw locations */}
          {locations.map(location => (
            <div
              key={location.id}
              className={`absolute p-2 transform -translate-x-1/2 -translate-y-1/2 ${
                location.type === 'classroom' 
                  ? 'bg-amber-600 text-white' 
                  : 'bg-blue-600 text-white'
              } ${
                startLocation?.id === location.id
                  ? 'ring-4 ring-green-300'
                  : 'cursor-pointer'
              } rounded-full h-10 w-10 flex items-center justify-center shadow-md
                transition-all hover:scale-110`}
              style={{
                left: `${location.position.x}px`,
                top: `${location.position.y}px`,
              }}
              onClick={() => handleLocationClick(location)}
              title={location.name}
            >
              <span className="text-xs font-bold truncate max-w-[32px]">
                {location.name.split(' ')[0][0]}
              </span>
            </div>
          ))}

          {/* Draw waypoints */}
          {waypoints.map((waypoint, index) => (
            <div
              key={index}
              className="absolute w-4 h-4 bg-amber-600 rounded-full transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              style={{ left: waypoint.x, top: waypoint.y }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteWaypoint(index);
              }}
            />
          ))}
        </div>

        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            {waypoints.length > 0 && (
              <button
                onClick={handleUndoLastWaypoint}
                className="px-4 py-2 bg-stone-100 text-stone-600 rounded-md hover:bg-stone-200 transition-colors"
              >
                Undo Last Waypoint
              </button>
            )}
          </div>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-stone-100 text-stone-600 rounded-md hover:bg-stone-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}; 